import { useState } from "react";
import { MoreHorizontal, AlertTriangle, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { format, isBefore, addDays } from "date-fns";
import type { Card as CardType } from "@/types/board";
import confetti from 'canvas-confetti';
import { supabase } from "@/lib/supabase";
import { boardEvents, BOARD_UPDATED } from "@/lib/events";
import { toast } from "@/components/ui/use-toast";
import DOMPurify from 'dompurify';
import { EditCardDialog } from "./EditCardDialog";

interface CardProps {
  card: CardType;
  index: number;
  columnId: string;
  onEdit: (columnId: string, cardId: string, title: string, description: string) => void;
  onDelete: (columnId: string, cardId: string) => void;
  onPriorityChange: (cardId: string, priority: 'critical' | 'important' | 'normal') => void;
}

export default function Card({
  card,
  index: _index,
  columnId,
  onEdit,
  onDelete,
  onPriorityChange,
}: CardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isMoving, setIsMoving] = useState(false);

  const formatPreviousNotes = (notes: string) => {
    if (!notes) return '';

    const entries = notes.split(/(?<=\n)\n(?=\[)/).filter(Boolean);

    const sortedEntries = entries
      .map(entry => {
        const timestampMatch = entry.match(/\[(.*?)\]/);
        if (timestampMatch) {
          const timestamp = new Date(timestampMatch[1]).getTime();
          return { timestamp, entry: entry.trim() };
        }
        return { timestamp: 0, entry: entry.trim() };
      })
      .sort((a, b) => b.timestamp - a.timestamp)
      .map(({ entry }) => entry);

    return sortedEntries.join('\n\n');
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      setIsMoving(true);

      // First, get the board_id through the current column
      const { data: currentColumn, error: columnError } = await supabase
        .from('columns')
        .select('board_id')
        .eq('id', columnId)
        .single();

      if (columnError) {
        console.error('Error fetching column:', columnError);
        return;
      }

      // Now fetch all columns in this board
      const { data: columns, error: columnsError } = await supabase
        .from('columns')
        .select('id, title')
        .eq('board_id', currentColumn.board_id);

      if (columnsError) {
        console.error('Error fetching columns:', columnsError);
        return;
      }

      if (!columns || columns.length === 0) {
        console.error('No columns found');
        return;
      }

      const targetColumn = columns.find(col => {
        const colTitle = col.title.toLowerCase().trim();
        switch (newStatus) {
          case 'todo':
            return colTitle === 'to do' || colTitle === 'todo';
          case 'in_progress':
            return colTitle === 'in progress' || colTitle.includes('progress');
          case 'done':
            return colTitle === 'done' || colTitle === 'completed';
          default:
            return false;
        }
      });

      if (!targetColumn) {
        console.error('Target column not found');
        return;
      }

      const { data: existingCards } = await supabase
        .from('cards')
        .select('order')
        .eq('column_id', targetColumn.id)
        .order('order', { ascending: false })
        .limit(1);

      const newOrder = existingCards && existingCards.length > 0
        ? (existingCards[0].order + 1)
        : 0;

      const { error: updateError } = await supabase
        .from('cards')
        .update({
          column_id: targetColumn.id,
          status: newStatus,
          order: newOrder,
          updated_at: new Date().toISOString()
        })
        .eq('id', card.id);

      if (updateError) {
        console.error('Error updating card:', updateError);
        return;
      }

      if (newStatus === 'done') {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
      }

      boardEvents.emit(BOARD_UPDATED);

    } catch (error) {
      console.error('Error updating card:', error);
    } finally {
      setIsMoving(false);
    }
  };

  const getPriorityColor = (priority: string | undefined) => {
    switch (priority) {
      case 'critical':
        return 'text-red-500';
      case 'important':
        return 'text-yellow-500';
      default:
        return 'text-gray-400';
    }
  };

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('cards')
        .delete()
        .eq('id', card.id);

      if (error) throw error;

      onDelete(columnId, card.id);
    } catch (error) {
      console.error('Error deleting card:', error);
      toast({
        title: "Error",
        description: "Failed to delete card. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getDueDateColor = () => {
    if (!card.due_date) return '';
    const dueDate = new Date(card.due_date);
    const now = new Date();

    if (isBefore(dueDate, now)) {
      return 'text-red-500'; // Overdue
    }
    if (isBefore(dueDate, addDays(now, 2))) {
      return 'text-yellow-500'; // Due soon (within 2 days)
    }
    return 'text-green-500'; // Due later
  };

  const handleCardUpdate = (cardId: string, title: string, description: string) => {
    onEdit(columnId, cardId, title, description);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-3 mb-2 border border-indigo-100/50 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div
          className="flex-1 min-w-0 overflow-hidden cursor-pointer"
          onClick={() => setIsEditing(true)}
          title="Click to expand / edit"
        >
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-900 break-words">{card.title ?? 'Untitled Card'}</h3>
            {card.due_date && (
              <div className={`flex items-center gap-1 text-xs ${getDueDateColor()}`}>
                <Calendar className="h-3 w-3" />
                <span>{format(new Date(card.due_date), 'MMM d, yyyy')}</span>
              </div>
            )}
          </div>
          {card.description && (
            <div className="mt-1 text-sm text-gray-500 max-h-[150px] overflow-y-auto">
              <div
                className="prose prose-sm max-w-none whitespace-pre-wrap break-words overflow-x-hidden pr-2"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(formatPreviousNotes(card.description))
                }}
              />
            </div>
          )}
          {card.priority && (
            <div className="mt-2 flex items-center gap-2">
              <AlertTriangle className={`h-4 w-4 ${getPriorityColor(card.priority)}`} />
              <span className={`text-xs capitalize ${getPriorityColor(card.priority)}`}>
                {card.priority}
              </span>
            </div>
          )}

          <div className="mt-3 border-t border-gray-200 pt-3" onClick={(e) => e.stopPropagation()}>
            <RadioGroup
              defaultValue={card.status || 'todo'}
              onValueChange={handleStatusChange}
              className="flex flex-wrap gap-x-4 gap-y-1"
              disabled={isMoving}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="todo" id={`todo-${card.id}`} />
                <Label htmlFor={`todo-${card.id}`} className="text-sm text-red-500">To Do</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="in_progress" id={`progress-${card.id}`} />
                <Label htmlFor={`progress-${card.id}`} className="text-sm text-yellow-500">In Progress</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="done" id={`done-${card.id}`} />
                <Label htmlFor={`done-${card.id}`} className="text-sm text-green-500">Done</Label>
              </div>
            </RadioGroup>
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 flex-shrink-0 ml-2">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setIsEditing(true)}>
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onPriorityChange(card.id, 'critical')}>
              Set Critical
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onPriorityChange(card.id, 'important')}>
              Set Important
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onPriorityChange(card.id, 'normal')}>
              Set Normal
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-600"
              onClick={handleDelete}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <EditCardDialog
        card={card}
        isOpen={isEditing}
        onOpenChange={setIsEditing}
        onUpdate={handleCardUpdate}
      />
    </div>
  );
} 